import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Cliente, Preventivo, Fattura, Appuntamento, DashboardStats, Impostazioni, VoceFrequente, CantiereFoto, SpesaCantiere } from '../models';

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private baseUrl = '/api';

  constructor(private http: HttpClient) {}

  // Dashboard
  getDashboardStats(): Observable<DashboardStats> {
    return this.http.get<DashboardStats>(`${this.baseUrl}/dashboard`);
  }

  // Clienti
  getClienti(): Observable<Cliente[]> {
    return this.http.get<Cliente[]>(`${this.baseUrl}/clienti`);
  }

  getCliente(id: number): Observable<Cliente> {
    return this.http.get<Cliente>(`${this.baseUrl}/clienti/${id}`);
  }

  createCliente(cliente: Cliente): Observable<Cliente> {
    return this.http.post<Cliente>(`${this.baseUrl}/clienti`, cliente);
  }

  updateCliente(id: number, cliente: Cliente): Observable<Cliente> {
    return this.http.put<Cliente>(`${this.baseUrl}/clienti/${id}`, cliente);
  }

  deleteCliente(id: number): Observable<any> {
    return this.http.delete(`${this.baseUrl}/clienti/${id}`);
  }

  // Preventivi
  getPreventivi(paramsObj?: any): Observable<Preventivo[]> {
    let params = new HttpParams();
    if (paramsObj) {
      Object.keys(paramsObj).forEach(key => {
        if (paramsObj[key]) params = params.set(key, paramsObj[key]);
      });
    }
    return this.http.get<Preventivo[]>(`${this.baseUrl}/preventivi`, { params });
  }

  getPreventivo(id: number): Observable<Preventivo> {
    return this.http.get<Preventivo>(`${this.baseUrl}/preventivi/${id}`);
  }

  createPreventivo(preventivo: Preventivo): Observable<Preventivo> {
    return this.http.post<Preventivo>(`${this.baseUrl}/preventivi`, preventivo);
  }

  updatePreventivo(id: number, preventivo: Preventivo): Observable<Preventivo> {
    return this.http.put<Preventivo>(`${this.baseUrl}/preventivi/${id}`, preventivo);
  }

  deletePreventivo(id: number): Observable<any> {
    return this.http.delete(`${this.baseUrl}/preventivi/${id}`);
  }

  downloadPreventivoPdf(id: number): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/preventivi/${id}/pdf`, { responseType: 'blob' });
  }

  sendPreventivoEmail(id: number): Observable<any> {
    return this.http.post(`${this.baseUrl}/preventivi/${id}/email`, {});
  }

  // Fatture
  getFatture(paramsObj?: any): Observable<Fattura[]> {
    let params = new HttpParams();
    if (paramsObj) {
      Object.keys(paramsObj).forEach(key => {
        if (paramsObj[key]) params = params.set(key, paramsObj[key]);
      });
    }
    return this.http.get<Fattura[]>(`${this.baseUrl}/fatture`, { params });
  }

  getFattura(id: number): Observable<Fattura> {
    return this.http.get<Fattura>(`${this.baseUrl}/fatture/${id}`);
  }

  createFattura(fattura: Fattura): Observable<Fattura> {
    return this.http.post<Fattura>(`${this.baseUrl}/fatture`, fattura);
  }

  updateFattura(id: number, fattura: Fattura): Observable<Fattura> {
    return this.http.put<Fattura>(`${this.baseUrl}/fatture/${id}`, fattura);
  }

  deleteFattura(id: number): Observable<any> {
    return this.http.delete(`${this.baseUrl}/fatture/${id}`);
  }

  downloadFatturaPdf(id: number): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/fatture/${id}/pdf`, { responseType: 'blob' });
  }

  // Appuntamenti
  getAppuntamenti(paramsObj?: any): Observable<Appuntamento[]> {
    let params = new HttpParams();
    if (paramsObj) {
      Object.keys(paramsObj).forEach(key => {
        if (paramsObj[key]) params = params.set(key, paramsObj[key]);
      });
    }
    return this.http.get<Appuntamento[]>(`${this.baseUrl}/appuntamenti`, { params });
  }

  getAppuntamento(id: number): Observable<Appuntamento> {
    return this.http.get<Appuntamento>(`${this.baseUrl}/appuntamenti/${id}`);
  }

  createAppuntamento(app: Appuntamento): Observable<Appuntamento> {
    return this.http.post<Appuntamento>(`${this.baseUrl}/appuntamenti`, app);
  }

  updateAppuntamento(id: number, app: Appuntamento): Observable<Appuntamento> {
    return this.http.put<Appuntamento>(`${this.baseUrl}/appuntamenti/${id}`, app);
  }

  deleteAppuntamento(id: number): Observable<any> {
    return this.http.delete(`${this.baseUrl}/appuntamenti/${id}`);
  }

  // Impostazioni Dati Lavoratore
  getImpostazioni(): Observable<Impostazioni> {
    return this.http.get<Impostazioni>(`${this.baseUrl}/impostazioni`);
  }

  updateImpostazioni(impostazioni: Impostazioni): Observable<Impostazioni> {
    return this.http.put<Impostazioni>(`${this.baseUrl}/impostazioni`, impostazioni);
  }

  // Voci Frequenti (Preset Lavori)
  getVociFrequenti(): Observable<VoceFrequente[]> {
    return this.http.get<VoceFrequente[]>(`${this.baseUrl}/voci-frequenti`);
  }

  createVoceFrequente(voce: VoceFrequente): Observable<VoceFrequente> {
    return this.http.post<VoceFrequente>(`${this.baseUrl}/voci-frequenti`, voce);
  }

  deleteVoceFrequente(id: number): Observable<any> {
    return this.http.delete(`${this.baseUrl}/voci-frequenti/${id}`);
  }

  // Cantiere Foto (Prima e Dopo)
  getCantiereFoto(clienteId?: number): Observable<CantiereFoto[]> {
    let params = new HttpParams();
    if (clienteId) params = params.set('cliente_id', clienteId);
    return this.http.get<CantiereFoto[]>(`${this.baseUrl}/cantiere-foto`, { params });
  }

  createCantiereFoto(foto: CantiereFoto): Observable<CantiereFoto> {
    return this.http.post<CantiereFoto>(`${this.baseUrl}/cantiere-foto`, foto);
  }

  deleteCantiereFoto(id: number): Observable<any> {
    return this.http.delete(`${this.baseUrl}/cantiere-foto/${id}`);
  }

  // Spese Cantiere (Materiali)
  getSpeseCantiere(clienteId?: number): Observable<SpesaCantiere[]> {
    let params = new HttpParams();
    if (clienteId) params = params.set('cliente_id', clienteId);
    return this.http.get<SpesaCantiere[]>(`${this.baseUrl}/spese-cantiere`, { params });
  }

  createSpesaCantiere(spesa: SpesaCantiere): Observable<SpesaCantiere> {
    return this.http.post<SpesaCantiere>(`${this.baseUrl}/spese-cantiere`, spesa);
  }

  deleteSpesaCantiere(id: number): Observable<any> {
    return this.http.delete(`${this.baseUrl}/spese-cantiere/${id}`);
  }
}
